import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { knowledgebaseService } from '../Services/knowledgebaseService';
import { KnowledgebaseDocumentStatus, KnowledgebaseSource } from '../Types';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { FileDlpModalDataOrigin } from '../Types/StoreTypes';
import { setFileDlpModalState } from '../Store/features/environment/environmentSlice';
import useModal from '../components/Modal/useModal';
import { RootState, useAppDispatch, useAppSelector } from '../store';

export default function useKnowledgebaseSources() {
    const { embeddingModelDlp } = useSelector((state: RootState) => state.settings);

    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const demo = useAppSelector((state: RootState) => state.environment.demoInstance as boolean);
    const dispatch = useAppDispatch();
    const [isLoadedSources, setIsLoadedSources] = useState(false);

    const sourceIdFromUrl = searchParams.get('source');
    const [isEditingSource, setIsEditingSource] = useState<{ status: boolean; id: string }>({
        status: !!sourceIdFromUrl,
        id: sourceIdFromUrl || '',
    });
    const [knowledgebaseSources, setKnowledgebaseSources] = useState<KnowledgebaseSource[]>([]);
    const [sourceProcessors, setSourceProcessors] = useState<any[]>([]);
    const [refreshCount, setRefreshCount] = useState<number>(0);

    const modal = useModal();

    const defaultSource = useMemo<KnowledgebaseSource>(
        () => ({
            id: '',
            name: '',
            type: '',
            status: '',
            interval: 'manual',
            configuration: {},
            nextExecution: '',
            lastExecution: '',
            documents: [],
            documentStatus: 'done',
        }),
        [],
    );
    const [tempSource, setTempSource] = useState<KnowledgebaseSource>(defaultSource);
    const [sourceSnapshot, setSourceSnapshot] = useState<KnowledgebaseSource | null>(null);

    const fetchSourceProcessors = async () => {
        const response = await knowledgebaseService.getSourceProcessors();
        const payload = response.data?.value ?? response.data;
        setSourceProcessors(payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {});
    };

    const handleTempSource = (name: string, value: string) => {
        setTempSource((prev) => ({ ...prev, [name]: value }));
    };

    const handleEditSource = useCallback((source: KnowledgebaseSource) => {
        setIsEditingSource((prev) => ({ status: !prev.status, id: source.id }));
        setSourceSnapshot(source);
        setSearchParams({ source: source.id });
    }, [setSearchParams]);

    const handleCloseSource = (source: KnowledgebaseSource | null) => {
        if (sourceHasChanges && source) {
            modal.confirm({
                title: i18n.translate({ id: 'modal.cancel.title', sourceName: 'KnowledgebaseSources' }),
                content: i18n.translate({ id: 'modal.cancel.text', sourceName: 'KnowledgebaseSources' }),
                size: 'small',
                onCancel: () => {
                    setIsEditingSource({ status: false, id: '' });
                    setIsLoadedSources(false);
                    setSearchParams({});
                },
                onConfirm: () => editSource(source),
                cancelText: i18n.translate({ id: 'button.cancel', sourceName: 'KnowledgebaseSources' }),
                confirmText: i18n.translate({ id: 'button.save', sourceName: 'KnowledgebaseSources' }),
            });
        } else {
            setIsEditingSource({ status: false, id: '' });
            setTempSource(defaultSource);
            setSearchParams({});
        }
    };

    const handleSourceChange = useCallback(
        (editingSource: KnowledgebaseSource, updates: Partial<KnowledgebaseSource>) => {
            setKnowledgebaseSources((prev) =>
                prev.map((source) => (source.id === editingSource.id ? { ...source, ...updates } : source)),
            );
        },
        [],
    );

    const handleDocumentUpload = async (files: File[]) => {
        const maxFileSize = 256 * 1024 * 1024;
        let failed = false;

        try {
            const uploadPromises = Array.from(files).map(async (blob) => {
                if (blob.size > maxFileSize) {
                    toast.error(
                        i18n.translate({
                            id: 'fileUpload.error.fileSize',
                            sourceName: 'FileUpload',
                            params: {
                                fileName: blob.name,
                            },
                        }),
                    );
                    failed = true;
                    return;
                }

                const file = new File([blob], blob.name);
                const formData = new FormData();
                formData.append('file', file);

                try {
                    const response = await knowledgebaseService.uploadFile(formData);
                    if (response.data.error) {
                        console.error(response.data.error);
                        toast.error(
                            i18n.translate({
                                id: 'fileUpload.error.upload',
                                sourceName: 'FileUpload',
                                params: {
                                    fileName: blob.name,
                                },
                            }),
                        );
                        failed = true;
                        return;
                    }
                    return {
                        name: response.data.name,
                        sha1: response.data.sha1,
                    };
                } catch (error) {
                    toast.error(
                        i18n.translate({
                            id: 'fileUpload.error.upload',
                            sourceName: 'FileUpload',
                            params: {
                                fileName: blob.name,
                            },
                        }),
                    );
                    console.error('File upload error:', error);
                    return null;
                }
            });

            const results = await Promise.all(uploadPromises);
            const successfulUploads = results.filter(
                (result): result is { name: string; sha1: string } => result !== null,
            );

            if (successfulUploads.length > 0 && !failed) {
                dispatch(
                    setFileDlpModalState({
                        state: 'open',
                        files: successfulUploads,
                        combinedDlp: embeddingModelDlp,
                        origin: FileDlpModalDataOrigin.Knowledgebase,
                        callback: async (files: any[]) => {
                            if (files.length === 0) {
                                return;
                            }

                            await knowledgebaseService.saveDocumentsToSource(files, isEditingSource.id);
                            toast.success(
                                i18n.translate({
                                    id: 'toast.addDocuments.success',
                                    sourceName: 'KnowledgebaseSources',
                                }),
                            );
                            setIsLoadedSources(false);
                        },
                    }),
                );
            }
        } catch (error) {
            toast.error(
                i18n.translate({
                    id: 'fileUpload.error.model',
                    sourceName: 'FileUpload',
                }),
            );
            console.error('Model loading error:', error);
        }
    };

    const handleRemoveExistingDocument = async (sourceIdentifier: string, documentIdentifier: string) => {
        try {
            await knowledgebaseService.removeExistingDocument(sourceIdentifier, documentIdentifier);
            setIsLoadedSources(false);
            toast.success(
                i18n.translate({
                    id: 'toast.removeDocument.success',
                    sourceName: 'KnowledgebaseSources',
                }),
            );
        } catch (error) {
            toast.error(
                i18n.translate({
                    id: 'toast.removeDocument.error',
                    sourceName: 'KnowledgebaseSources',
                }),
            );
        }
    };

    const syncSourceNow = async (source: any) => {
        try {
            const result = await knowledgebaseService.syncKnowledgebaseSourceNow(source.id);
            handleSourceChange(source, {
                nextExecution: result.data.nextExecution,
            });

            toast.success(
                i18n.translate({
                    id: 'toast.syncNow.success',
                    sourceName: 'KnowledgebaseSources',
                }),
            );
        } catch (error) {
            toast.error(
                i18n.translate({
                    id: 'toast.syncNow.error',
                    sourceName: 'KnowledgebaseSources',
                }),
            );
        }
    };

    const deleteSource = useCallback(
        (source: KnowledgebaseSource) => {
            const handleDeleteSource = async (source: KnowledgebaseSource) => {
                try {
                    await knowledgebaseService.deleteKnowledgebaseSource(source.id);

                    if (isEditingSource.status) {
                        setIsEditingSource({ status: false, id: '' });
                        setSearchParams({});
                    }

                    setIsLoadedSources(false);
                    toast.success(
                        i18n.translate({
                            id: 'toast.delete.success',
                            sourceName: 'KnowledgebaseSources',
                        }),
                    );
                } catch (error) {
                    toast.error(
                        i18n.translate({
                            id: 'toast.delete.error',
                            sourceName: 'KnowledgebaseSources',
                        }),
                    );
                }
            };

            modal.deleteConfirm({
                title: i18n.translate({ id: 'modal.delete.title', sourceName: 'KnowledgebaseSources' }),
                content: i18n.translate({ id: 'modal.delete.text', sourceName: 'KnowledgebaseSources' }),
                size: 'small',
                onConfirm: () => handleDeleteSource(source),
            });
        },
        [i18n, isEditingSource, modal, setSearchParams],
    );

    const editSource = useCallback(
        async (source: KnowledgebaseSource, isNewSource: boolean = false) => {
            try {
                await knowledgebaseService.createKnowledgebaseSource(source, id!).then((response) => {
                    if (response.data.error) {
                        toast.error(
                            i18n.translate({
                                id: 'toast.save.error',
                                sourceName: 'KnowledgebaseSources',
                                params: {
                                    sourceName: source.name,
                                },
                            }),
                        );
                    } else {
                        toast.success(
                            i18n.translate({
                                id: 'toast.save.success',
                                sourceName: 'KnowledgebaseSources',
                                params: {
                                    sourceName: source.name,
                                },
                            }),
                        );
                    }

                    if (isNewSource) {
                        setKnowledgebaseSources((prev) => [...prev, response.data]);
                        handleEditSource(response.data);
                        setTempSource(defaultSource);

                        return;
                    }

                    setTempSource(defaultSource);
                    setIsLoadedSources(false);
                    setSourceSnapshot(response.data);
                });
            } catch (error) {
                toast.error(
                    i18n.translate({
                        id: 'toast.save.error',
                        sourceName: 'KnowledgebaseSources',
                        params: {
                            sourceName: source.name,
                        },
                    }),
                );
            }
        },
        [id, i18n, defaultSource, handleEditSource],
    );

    const validateSource = async (source: KnowledgebaseSource) => {
        const result = await knowledgebaseService.sourceValidate(source);

        modal.alert({
            title: i18n.translate({ id: 'modal.validation.title', sourceName: 'KnowledgebaseSources' }),
            content: result.data.success
                ? i18n.translate({ id: 'modal.validation.success', sourceName: 'KnowledgebaseSources' })
                : result.data.message,
            size: 'small',
            buttonText: i18n.translate({ id: 'button.modalClose', sourceName: 'KnowledgebaseSources' }),
            onClose: () => modal.close(),
        });
    };

    const sourceHasChanges = useMemo(() => {
        if (!sourceSnapshot) {
            return false;
        }

        const source = knowledgebaseSources.find((source) => source.id === sourceSnapshot.id);

        if (!source) {
            return false;
        }

        const pickRelevantFields = (source: KnowledgebaseSource) => {
            const { name, configuration } = source;
            return { name, configuration };
        };
        return JSON.stringify(pickRelevantFields(sourceSnapshot)) !== JSON.stringify(pickRelevantFields(source));
    }, [sourceSnapshot, knowledgebaseSources]);

    useEffect(() => {
        const loadSourcesAndProcessors = async () => {
            if (sourceProcessors.length === 0) {
                await fetchSourceProcessors();
            }

            if (id && id !== 'new' && !isLoadedSources) {
                const response = await knowledgebaseService.getKnowledgebaseSources(id);
                setKnowledgebaseSources(response.data);
                setIsLoadedSources(true);
            } else if (id === 'new' && !isLoadedSources) {
                setIsLoadedSources(true);
            }
        };
        loadSourcesAndProcessors();
    }, [id, sourceProcessors.length, isLoadedSources]);

    useEffect(() => {
        const currentSourceIdFromUrl = searchParams.get('source');
        if (currentSourceIdFromUrl !== isEditingSource.id) {
            if (currentSourceIdFromUrl) {
                setIsEditingSource({ status: true, id: currentSourceIdFromUrl });
            } else if (isEditingSource.status) {
                setIsEditingSource({ status: false, id: '' });
                setSourceSnapshot(null);
            }
        }
    }, [searchParams, isEditingSource.id, isEditingSource.status]);

    useEffect(() => {
        if (
            isEditingSource.status &&
            isEditingSource.id &&
            isLoadedSources &&
            knowledgebaseSources.length > 0 &&
            !sourceSnapshot
        ) {
            const sourceToOpen = knowledgebaseSources.find((source) => source.id === isEditingSource.id);
            if (sourceToOpen) {
                setSourceSnapshot(sourceToOpen);
            }
        }
    }, [isEditingSource.id, isEditingSource.status, isLoadedSources, knowledgebaseSources, sourceSnapshot]);

    useEffect(() => {
        let interval: NodeJS.Timeout;

        if (isEditingSource.status && id && id !== 'new') {
            interval = setInterval(() => {
                setRefreshCount(refreshCount + 1);
            }, 10000);
        }

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [isEditingSource.status, id, refreshCount]);

    const sourceActions = {
        syncSourceNow,
        deleteSource,
        editSource,
        validateSource,
    };

    return {
        sourceProcessors,
        tempSource,
        isLoadedSources,
        isEditingSource,
        knowledgebaseSources,
        handleTempSource,
        handleEditSource,
        handleCloseSource,
        handleSourceChange,
        handleDocumentUpload,
        handleRemoveExistingDocument,
        sourceActions,
        sourceHasChanges,
        getKnowledgebaseDocuments: useMemo(
            () =>
                async (
                    status: KnowledgebaseDocumentStatus | null,
                    page: number,
                    itemsPerPage: number,
                    _: any,
                    query?: string | null,
                ) => {
                    const editingSource = knowledgebaseSources.find((source) => source.id === isEditingSource.id);
                    let fileRes: { data: { total: number; data: any[] } } = { data: { total: 0, data: [] } };

                    if (demo || !editingSource) {
                        return { data: { total: 0, data: [] } };
                    }

                    if (refreshCount >= 0) {
                        const maxFilePageCount = Math.ceil(fileRes.data.total / itemsPerPage);
                        page = Math.max((maxFilePageCount >= 1 ? 1 : 0) + page - maxFilePageCount, 1);

                        const res = await knowledgebaseService.getKnowledgebaseDocuments(
                            editingSource.id,
                            status,
                            page,
                            itemsPerPage,
                            query,
                        );

                        return {
                            data: {
                                total: res.data.total,
                                data: res.data.data,
                            },
                        };
                    }
                },
            [isEditingSource, knowledgebaseSources, refreshCount, demo],
        ),
    };
}
